import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import API, { formatApiError } from "@/lib/api";
import MediaLibrary from "@/components/MediaLibrary";

const EMPTY_SESSION = {
  title: "", description: "", category: "Workshop", date: "", time: "", duration: "90 mins",
  price: 499, banner_url: "", zoom_link: "", whatsapp_link: "", is_active: true,
};

const StatusBadge = ({ status }) => {
  const styles = {
    paid: "bg-[#E8F3EE] text-[#2D5A47]",
    failed: "bg-red-100 text-red-700",
    created: "bg-[#F9EBEA] text-[#D98E32]",
  };
  return (
    <span data-testid={`status-badge-${status}`} className={`px-2.5 py-1 rounded-full text-xs font-semibold ${styles[status] || styles.created}`}>
      {status === "created" ? "pending" : status}
    </span>
  );
};

const SessionFormDialog = ({ open, onClose, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY_SESSION);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const bannerInputRef = useRef(null);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY_SESSION, ...initial } : EMPTY_SESSION);
  }, [open, initial]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/admin/upload", fd);
      setForm((f) => ({ ...f, banner_url: data.url }));
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, price: parseInt(form.price, 10) || 0 };
      if (isEdit) {
        await API.put(`/admin/sessions/${initial.id}`, payload);
        toast.success("Session updated");
      } else {
        await API.post("/admin/sessions", payload);
        toast.success("Session created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { key: "title", label: "Title", testid: "session-form-title", required: true },
    { key: "category", label: "Category (e.g. Money / Health / Relationships)", testid: "session-form-category" },
    { key: "date", label: "Date", testid: "session-form-date", type: "date", required: true },
    { key: "time", label: "Time (e.g. 7:00 PM IST)", testid: "session-form-time", required: true },
    { key: "duration", label: "Duration", testid: "session-form-duration" },
    { key: "price", label: "Price (INR)", testid: "session-form-price", type: "number", required: true },
    { key: "banner_url", label: "Banner image URL (or upload above)", testid: "session-form-banner" },
    { key: "zoom_link", label: "Zoom meeting link", testid: "session-form-zoom" },
    { key: "whatsapp_link", label: "WhatsApp group invite link", testid: "session-form-whatsapp" },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#FDFBF7] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="session-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2D5A47]">
            {isEdit ? "Edit Session" : "Add New Session"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="session-form">
          <div>
            <Label className="text-xs text-[#5A6E63]">Banner image</Label>
            <div className="mt-1 flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" data-testid="banner-upload-btn"
                onClick={() => bannerInputRef.current?.click()} disabled={uploading}
                className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
                {uploading ? "Uploading..." : "Upload Image"}
              </Button>
              <input ref={bannerInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden" data-testid="banner-file-input" onChange={handleBannerUpload} />
              <Button type="button" variant="outline" size="sm" data-testid="banner-library-btn"
                onClick={() => setShowLibrary((v) => !v)}
                className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
                {showLibrary ? "Hide Library" : "Choose from Library"}
              </Button>
              {form.banner_url && (
                <img src={form.banner_url} alt="Banner preview" data-testid="banner-preview"
                  className="h-10 w-16 rounded-lg object-cover border border-[#2D5A47]/20" />
              )}
            </div>
            {showLibrary && (
              <div className="mt-3 p-3 rounded-2xl bg-[#FAF6F0] border border-[#2D5A47]/10" data-testid="banner-library">
                <MediaLibrary selectedUrl={form.banner_url}
                  onSelect={(url) => { setForm((f) => ({ ...f, banner_url: url })); toast.success("Banner selected"); }} />
              </div>
            )}
          </div>
          {fields.map(({ key, label, testid, type = "text", required }) => (
            <div key={key}>
              <Label htmlFor={testid} className="text-xs text-[#5A6E63]">{label}</Label>
              <Input id={testid} data-testid={testid} type={type} required={required} value={form[key]}
                onChange={set(key)} className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
            </div>
          ))}
          <div>
            <Label htmlFor="session-form-description" className="text-xs text-[#5A6E63]">Description</Label>
            <Textarea id="session-form-description" data-testid="session-form-description" value={form.description}
              onChange={set("description")} rows={3} className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
          </div>
          <div className="flex items-center gap-3 py-1">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              data-testid="session-form-active" />
            <span className="text-sm text-[#5A6E63]">Visible on landing page</span>
          </div>
          <Button type="submit" data-testid="session-form-save" disabled={saving}
            className="w-full rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] h-11">
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Session"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SessionsTab = () => {
  const [sessions, setSessions] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    API.get("/admin/sessions").then((res) => setSessions(res.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this session? This cannot be undone.")) return;
    try {
      await API.delete(`/admin/sessions/${id}`);
      toast.success("Session deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-sessions-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#5A6E63]">{sessions.length} session(s)</p>
        <Button data-testid="admin-add-session-btn" onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7]">
          <Plus size={15} className="mr-1.5" /> Add Session
        </Button>
      </div>
      <div className="bg-[#FAF6F0] rounded-2xl soft-shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Session</TableHead>
              <TableHead>Date &amp; Time</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((s) => (
              <TableRow key={s.id} data-testid={`admin-session-row-${s.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {s.banner_url && <img src={s.banner_url} alt="" className="w-12 h-9 rounded-lg object-cover" />}
                    <div>
                      <p className="font-semibold text-[#2D5A47] text-sm">{s.title}</p>
                      <p className="text-xs text-[#5A6E63]">{s.category}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-[#5A6E63]">{s.date}<br />{s.time}</TableCell>
                <TableCell className="text-sm font-semibold">&#8377;{s.price}</TableCell>
                <TableCell>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${s.is_active ? "bg-[#E8F3EE] text-[#2D5A47]" : "bg-gray-100 text-gray-500"}`}>
                    {s.is_active ? "live" : "hidden"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" data-testid={`edit-session-${s.id}`}
                    onClick={() => { setEditing(s); setDialogOpen(true); }}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" data-testid={`delete-session-${s.id}`}
                    onClick={() => remove(s.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <SessionFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    API.get("/admin/users").then((res) => setUsers(res.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div data-testid="admin-users-tab">
      <p className="text-sm text-[#5A6E63] mb-4">{users.length} registration(s)</p>
      <div className="bg-[#FAF6F0] rounded-2xl soft-shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Age</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Session</TableHead>
              <TableHead>Payment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <TableCell className="font-semibold text-sm text-[#2D5A47]">{u.name}</TableCell>
                <TableCell className="text-xs text-[#5A6E63]">{u.email}<br />{u.mobile}</TableCell>
                <TableCell className="text-sm">{u.age}</TableCell>
                <TableCell className="text-xs text-[#5A6E63]">{u.city}, {u.state}<br />{u.pincode}</TableCell>
                <TableCell className="text-xs">{u.session_title}</TableCell>
                <TableCell><StatusBadge status={u.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const PaymentsTab = () => {
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    API.get("/admin/payments").then((res) => setPayments(res.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <div data-testid="admin-payments-tab">
      <p className="text-sm text-[#5A6E63] mb-4">{payments.length} payment record(s)</p>
      <div className="bg-[#FAF6F0] rounded-2xl soft-shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Session</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Payment / Order ID</TableHead>
              <TableHead>Date &amp; Time</TableHead>
              <TableHead>Receipt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.registration_id} data-testid={`admin-payment-row-${p.registration_id}`}>
                <TableCell className="text-xs"><span className="font-semibold text-[#2D5A47]">{p.name}</span><br />{p.email}</TableCell>
                <TableCell className="text-xs">{p.session_title}</TableCell>
                <TableCell className="text-sm font-semibold">&#8377;{p.amount}</TableCell>
                <TableCell>
                  <StatusBadge status={p.status} />
                  {p.failure_reason && (
                    <p className="text-[10px] text-red-500 mt-1 max-w-[180px]" data-testid={`failure-reason-${p.registration_id}`}>{p.failure_reason}</p>
                  )}
                </TableCell>
                <TableCell className="text-xs text-[#5A6E63]">{p.razorpay_payment_id || p.razorpay_order_id || "-"}</TableCell>
                <TableCell className="text-xs text-[#5A6E63]">
                  {p.paid_at ? new Date(p.paid_at).toLocaleString("en-IN") : p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "-"}
                </TableCell>
                <TableCell className="text-xs">{p.email_sent ? "emailed" : "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const ConsultancyTab = () => {
  const [bookings, setBookings] = useState([]);
  const [services, setServices] = useState([]);
  const [priceEdits, setPriceEdits] = useState({});

  const load = useCallback(() => {
    API.get("/admin/consultancy/bookings").then((res) => setBookings(res.data)).catch((e) => toast.error(formatApiError(e)));
    API.get("/admin/consultancy/services").then((res) => {
      setServices(res.data);
      setPriceEdits(Object.fromEntries(res.data.map((s) => [s.id, s.price])));
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const savePrice = async (s) => {
    try {
      await API.put(`/admin/consultancy/services/${s.id}`, {
        price: parseInt(priceEdits[s.id], 10) || 0,
        is_active: s.is_active,
      });
      toast.success(`${s.name} updated`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-consultancy-tab">
      <div className="grid sm:grid-cols-3 gap-4 mb-8">
        {services.map((s) => (
          <div key={s.id} className="bg-[#FAF6F0] rounded-2xl soft-shadow p-4" data-testid={`consultancy-service-${s.id}`}>
            <p className="font-semibold text-sm text-[#2D5A47]">{s.name}</p>
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-[#5A6E63]">&#8377;</span>
              <Input type="number" value={priceEdits[s.id] ?? s.price}
                data-testid={`consultancy-price-${s.id}`}
                onChange={(e) => setPriceEdits((p) => ({ ...p, [s.id]: e.target.value }))}
                className="h-8 w-24 bg-[#FDFBF7] border-[#2D5A47]/20 text-sm" />
              <Button size="sm" data-testid={`consultancy-price-save-${s.id}`} onClick={() => savePrice(s)}
                className="rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] h-8">
                Save
              </Button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-sm text-[#5A6E63] mb-4">{bookings.length} booking(s)</p>
      <div className="bg-[#FAF6F0] rounded-2xl soft-shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Service</TableHead>
              <TableHead>Details</TableHead>
              <TableHead>Time Slot</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Booked On</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.map((b) => (
              <TableRow key={b.id} data-testid={`consultancy-booking-row-${b.id}`}>
                <TableCell className="font-semibold text-sm text-[#2D5A47]">{b.name}</TableCell>
                <TableCell className="text-xs text-[#5A6E63]">{b.email}<br />{b.phone}</TableCell>
                <TableCell className="text-xs font-medium">{b.service_name}</TableCell>
                <TableCell className="text-xs text-[#5A6E63]">
                  {b.service_id === "astrology" ? (
                    <span>DOB: {b.dob}<br />{b.birth_time} &middot; {b.birth_place}</span>
                  ) : b.document_url ? (
                    <a href={b.document_url} target="_blank" rel="noopener noreferrer"
                      data-testid={`consultancy-doc-${b.id}`} className="text-[#D98E32] underline">View document</a>
                  ) : "-"}
                </TableCell>
                <TableCell className="text-xs">{b.slot_time}<br /><span className="text-[#5A6E63]">{b.slot_period}</span></TableCell>
                <TableCell className="text-sm font-semibold">&#8377;{b.amount}</TableCell>
                <TableCell><StatusBadge status={b.status} /></TableCell>
                <TableCell className="text-xs text-[#5A6E63]">
                  {b.created_at ? new Date(b.created_at).toLocaleString("en-IN") : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const SiteSettingsTab = () => {
  const [heroImage, setHeroImage] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    API.get("/site-config").then((res) => setHeroImage(res.data.hero_image || "")).catch(() => {});
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/admin/upload", fd);
      setHeroImage(data.url);
      toast.success("Image uploaded - click Save to apply");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await API.put("/admin/site-config", { hero_image: heroImage });
      toast.success("Homepage hero image updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-tab" className="max-w-lg">
      <p className="text-sm text-[#5A6E63] mb-4">
        Change the hero image shown on the homepage whenever you like. Upload a photo or paste an image URL, then save.
      </p>
      <div className="bg-[#FAF6F0] rounded-2xl soft-shadow p-6 space-y-4">
        {heroImage ? (
          <img src={heroImage} alt="Hero preview" data-testid="hero-image-preview"
            className="w-full aspect-square max-w-xs rounded-2xl object-cover border border-[#2D5A47]/20" />
        ) : (
          <p className="text-xs text-[#5A6E63]" data-testid="hero-image-default-note">No custom image set - the default meditation photo is shown.</p>
        )}
        <div className="flex items-center gap-3">
          <Button type="button" variant="outline" size="sm" data-testid="hero-upload-btn"
            onClick={() => fileRef.current?.click()} disabled={uploading}
            className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
            {uploading ? "Uploading..." : "Upload Image"}
          </Button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden" data-testid="hero-file-input" onChange={handleUpload} />
        </div>
        <div>
          <Label htmlFor="hero-image-input" className="text-xs text-[#5A6E63]">Hero image URL</Label>
          <Input id="hero-image-input" data-testid="hero-image-input" value={heroImage}
            onChange={(e) => setHeroImage(e.target.value)} placeholder="https://..."
            className="mt-1 bg-[#FDFBF7] border-[#2D5A47]/20" />
        </div>
        <Button onClick={save} disabled={saving} data-testid="hero-save-btn"
          className="rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7]">
          {saving ? "Saving..." : "Save Hero Image"}
        </Button>
      </div>
    </div>
  );
};

const EMPTY_COURSE = { title: "", description: "", price: 999, banner_url: "", lessons: [], is_active: true };

const CourseFormDialog = ({ open, onClose, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY_COURSE);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const bannerInputRef = useRef(null);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY_COURSE, ...initial, lessons: initial.lessons || [] } : EMPTY_COURSE);
  }, [open, initial]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const setLesson = (i, key) => (e) => setForm((f) => {
    const lessons = [...f.lessons];
    lessons[i] = { ...lessons[i], [key]: e.target.value };
    return { ...f, lessons };
  });
  const addLesson = () => setForm((f) => ({ ...f, lessons: [...f.lessons, { title: "", video_url: "" }] }));
  const removeLesson = (i) => setForm((f) => ({ ...f, lessons: f.lessons.filter((_, idx) => idx !== i) }));

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/admin/upload", fd);
      setForm((f) => ({ ...f, banner_url: data.url }));
      toast.success("Banner uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const lessons = form.lessons
      .map((l) => ({ title: (l.title || "").trim(), video_url: (l.video_url || "").trim() }))
      .filter((l) => l.title && l.video_url);
    if (lessons.length === 0) {
      toast.error("Add at least one lesson with a title and video link");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: parseInt(form.price, 10) || 0, lessons };
      if (isEdit) {
        await API.put(`/admin/courses/${initial.id}`, payload);
        toast.success("Course updated");
      } else {
        await API.post("/admin/courses", payload);
        toast.success("Course created");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#FDFBF7] rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="course-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#2D5A47]">
            {isEdit ? "Edit Course" : "Add New Course"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="course-form">
          <div>
            <Label className="text-xs text-[#5A6E63]">Banner image</Label>
            <div className="mt-1 flex items-center gap-3">
              <Button type="button" variant="outline" size="sm" data-testid="course-banner-upload-btn"
                onClick={() => bannerInputRef.current?.click()} disabled={uploading}
                className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
                {uploading ? "Uploading..." : "Upload Image"}
              </Button>
              <input ref={bannerInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden" data-testid="course-banner-file-input" onChange={handleBannerUpload} />
              <Button type="button" variant="outline" size="sm" data-testid="course-banner-library-btn"
                onClick={() => setShowLibrary((v) => !v)}
                className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE]">
                {showLibrary ? "Hide Library" : "Choose from Library"}
              </Button>
              {form.banner_url && (
                <img src={form.banner_url} alt="Banner preview" data-testid="course-banner-preview"
                  className="h-10 w-16 rounded-lg object-cover border border-[#2D5A47]/20" />
              )}
            </div>
            {showLibrary && (
              <div className="mt-3 p-3 rounded-2xl bg-[#FAF6F0] border border-[#2D5A47]/10" data-testid="course-banner-library">
                <MediaLibrary selectedUrl={form.banner_url}
                  onSelect={(url) => { setForm((f) => ({ ...f, banner_url: url })); toast.success("Banner selected"); }} />
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="course-form-title" className="text-xs text-[#5A6E63]">Title</Label>
            <Input id="course-form-title" data-testid="course-form-title" required value={form.title}
              onChange={set("title")} className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
          </div>
          <div>
            <Label htmlFor="course-form-price" className="text-xs text-[#5A6E63]">Price (INR)</Label>
            <Input id="course-form-price" data-testid="course-form-price" type="number" required value={form.price}
              onChange={set("price")} className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
          </div>
          <div>
            <Label htmlFor="course-form-banner-url" className="text-xs text-[#5A6E63]">Banner image URL (or upload above)</Label>
            <Input id="course-form-banner-url" data-testid="course-form-banner" value={form.banner_url}
              onChange={set("banner_url")} className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
          </div>
          <div>
            <Label htmlFor="course-form-description" className="text-xs text-[#5A6E63]">Description</Label>
            <Textarea id="course-form-description" data-testid="course-form-description" value={form.description}
              onChange={set("description")} rows={3} className="mt-1 bg-[#FAF6F0] border-[#2D5A47]/20" />
          </div>

          <div className="pt-1">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-[#5A6E63]">Video lessons (YouTube / Vimeo links)</Label>
              <Button type="button" size="sm" variant="outline" data-testid="course-add-lesson-btn" onClick={addLesson}
                className="rounded-full border-[#2D5A47]/30 text-[#2D5A47] hover:bg-[#E8F3EE] h-8">
                <Plus size={14} className="mr-1" /> Add lesson
              </Button>
            </div>
            <div className="space-y-2">
              {form.lessons.length === 0 && (
                <p className="text-xs text-[#8a9a90]" data-testid="course-no-lessons">No lessons yet. Add your first lesson.</p>
              )}
              {form.lessons.map((l, i) => (
                <div key={i} className="rounded-2xl bg-[#FAF6F0] border border-[#2D5A47]/10 p-3 space-y-2" data-testid={`course-lesson-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#2D5A47]">Lesson {i + 1}</span>
                    <Button type="button" variant="ghost" size="sm" data-testid={`course-remove-lesson-${i}`}
                      onClick={() => removeLesson(i)} className="text-red-600 hover:text-red-700 h-7 px-2">
                      <Trash2 size={13} />
                    </Button>
                  </div>
                  <Input data-testid={`course-lesson-title-${i}`} placeholder="Lesson title" value={l.title || ""}
                    onChange={setLesson(i, "title")} className="bg-[#FDFBF7] border-[#2D5A47]/20 h-9 text-sm" />
                  <Input data-testid={`course-lesson-url-${i}`} placeholder="https://youtube.com/watch?v=... or vimeo.com/..."
                    value={l.video_url || ""} onChange={setLesson(i, "video_url")}
                    className="bg-[#FDFBF7] border-[#2D5A47]/20 h-9 text-sm" />
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 py-1">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm((f) => ({ ...f, is_active: v }))}
              data-testid="course-form-active" />
            <span className="text-sm text-[#5A6E63]">Visible on landing page</span>
          </div>
          <Button type="submit" data-testid="course-form-save" disabled={saving}
            className="w-full rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7] h-11">
            {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Course"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CoursesTab = () => {
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    API.get("/admin/courses").then((res) => setCourses(res.data)).catch((e) => toast.error(formatApiError(e)));
    API.get("/admin/course-enrollments").then((res) => setEnrollments(res.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this course? This cannot be undone.")) return;
    try {
      await API.delete(`/admin/courses/${id}`);
      toast.success("Course deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-courses-tab">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[#5A6E63]">{courses.length} course(s)</p>
        <Button data-testid="admin-add-course-btn" onClick={() => { setEditing(null); setDialogOpen(true); }}
          className="rounded-full bg-[#D98E32] hover:bg-[#c17a24] text-[#FDFBF7]">
          <Plus size={15} className="mr-1.5" /> Add Course
        </Button>
      </div>
      <div className="bg-[#FAF6F0] rounded-2xl soft-shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Course</TableHead>
              <TableHead>Lessons</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {courses.map((c) => (
              <TableRow key={c.id} data-testid={`admin-course-row-${c.id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {c.banner_url && <img src={c.banner_url} alt="" className="w-12 h-9 rounded-lg object-cover" />}
                    <p className="font-semibold text-[#2D5A47] text-sm">{c.title}</p>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-[#5A6E63]">{(c.lessons || []).length}</TableCell>
                <TableCell className="text-sm font-semibold">&#8377;{c.price}</TableCell>
                <TableCell>
                  <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${c.is_active ? "bg-[#E8F3EE] text-[#2D5A47]" : "bg-gray-100 text-gray-500"}`}>
                    {c.is_active ? "live" : "hidden"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" data-testid={`edit-course-${c.id}`}
                    onClick={() => { setEditing(c); setDialogOpen(true); }}>
                    <Pencil size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" data-testid={`delete-course-${c.id}`}
                    onClick={() => remove(c.id)} className="text-red-600 hover:text-red-700">
                    <Trash2 size={14} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <p className="text-sm text-[#5A6E63] mt-8 mb-4">{enrollments.length} course enrollment(s)</p>
      <div className="bg-[#FAF6F0] rounded-2xl soft-shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Course</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Payment</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {enrollments.map((e) => (
              <TableRow key={e.id} data-testid={`admin-enrollment-row-${e.id}`}>
                <TableCell className="font-semibold text-sm text-[#2D5A47]">{e.name}</TableCell>
                <TableCell className="text-xs text-[#5A6E63]">{e.email}<br />{e.mobile}</TableCell>
                <TableCell className="text-xs">{e.course_title}</TableCell>
                <TableCell className="text-sm font-semibold">&#8377;{e.amount}</TableCell>
                <TableCell><StatusBadge status={e.status} /></TableCell>
                <TableCell className="text-xs text-[#5A6E63]">
                  {e.paid_at ? new Date(e.paid_at).toLocaleString("en-IN") : e.created_at ? new Date(e.created_at).toLocaleString("en-IN") : "-"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <CourseFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} initial={editing} onSaved={load} />
    </div>
  );
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [adminName, setAdminName] = useState("");

  useEffect(() => {
    API.get("/auth/me")
      .then((res) => { setAuthed(true); setAdminName(res.data.name); })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const logout = async () => {
    await API.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!authed) {
    return <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center text-[#5A6E63]" data-testid="admin-loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="admin-panel">
      <header className="border-b border-[rgba(45,90,71,0.15)] bg-[#FDFBF7]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-4 sm:px-6 h-16">
          <Link to="/" className="flex items-center" data-testid="admin-logo">
            <img src="/logo.jpeg" alt="Binfinite Mind" className="h-10 mix-blend-multiply" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#5A6E63] hidden sm:block">Hi, {adminName}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="admin-logout-btn" className="text-[#5A6E63]">
              <LogOut size={14} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#2D5A47]" data-testid="admin-panel-heading">Admin Panel</h1>
        <Tabs defaultValue="sessions" className="mt-6">
          <TabsList className="bg-[#E8F3EE]">
            <TabsTrigger value="sessions" data-testid="tab-sessions">Live Sessions</TabsTrigger>
            <TabsTrigger value="courses" data-testid="tab-courses">Video Courses</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">User Registrations</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="consultancy" data-testid="tab-consultancy">Consultancy</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Site Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="sessions" className="mt-6"><SessionsTab /></TabsContent>
          <TabsContent value="courses" className="mt-6"><CoursesTab /></TabsContent>
          <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
          <TabsContent value="payments" className="mt-6"><PaymentsTab /></TabsContent>
          <TabsContent value="consultancy" className="mt-6"><ConsultancyTab /></TabsContent>
          <TabsContent value="media" className="mt-6">
            <div className="bg-[#FDFBF7] rounded-3xl p-5 sm:p-6 soft-shadow border border-[rgba(45,90,71,0.1)]">
              <h2 className="font-display text-xl text-[#2D5A47] mb-1">Media Library</h2>
              <p className="text-xs text-[#5A6E63] mb-4">Upload and manage banner images. Pick any of these when editing a session.</p>
              <MediaLibrary />
            </div>
          </TabsContent>
          <TabsContent value="settings" className="mt-6"><SiteSettingsTab /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
